/* TCG-Cases – 3D Acryl-Case im Hero (eigene Datei, kein Horizon-Code) */
(() => {
  if (customElements.get('tcg-acrylic-case')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  class TcgAcrylicCase extends HTMLElement {
    connectedCallback() {
      this.rig = this.querySelector('.tcg-case__rig');
      this.stage = this.querySelector('.tcg-case__stage');
      if (!this.rig || !this.stage) return;
      this.anchor = this.querySelector('[data-tag-anchor]');
      this.screwTag = this.querySelector('.tcg-case__tag--4');

      this.baseRy = parseFloat(this.dataset.angle || '-28');
      this.baseRx = -10;
      this.visible = true;
      this.paused = [];
      this.start = performance.now();

      // Performance: Schildgröße nur bei Größenänderung messen (nicht in jedem Frame)
      this.tagSize = null;
      this.tagFlip = null;
      if (this.screwTag && 'ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          this.tagSize = null;
        });
        this.ro.observe(this.screwTag);
        this.ro.observe(this.stage);
      }

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.reduced.matches) {
        this.apply(this.baseRx, this.baseRy);
        this.placeScrewTag(this.measureTag());
        return;
      }

      // hold the assembly intro until at least half of the case is visible
      const r0 = this.getBoundingClientRect();
      if (r0.top > window.innerHeight * 0.85 || r0.bottom < 0) this.classList.add('is-waiting');

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.setAnimationsPaused(!this.visible);
        if (this.visible && this.classList.contains('is-waiting') && entry.intersectionRatio >= 0.45) {
          this.classList.remove('is-waiting');
          this.start = performance.now();
        }
        if (this.visible) this.kick();
      }, { threshold: [0, 0.45] });
      this.io.observe(this);

      // Performance: Case ausserhalb des Bildschirms komplett aus dem Rendering nehmen (Scrollen bleibt flüssig)
      this.ioHide = new IntersectionObserver(
        ([entry]) => {
          this.style.visibility = entry.isIntersecting ? '' : 'hidden';
        },
        { rootMargin: '250px 0px' }
      );
      this.ioHide.observe(this);

      this.onVis = () => {
        if (!document.hidden) this.kick();
      };
      document.addEventListener('visibilitychange', this.onVis);

      this.kick();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      this.raf = null;
      if (this.io) this.io.disconnect();
      if (this.ioHide) this.ioHide.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
    }

    kick() {
      if (this.raf) return;
      this.raf = requestAnimationFrame((t) => this.tick(t));
    }

    tick(now) {
      this.raf = null;
      if (!this.visible || document.hidden) return;

      // 1) Erst Layout-Werte lesen (Stand des letzten Frames) …
      const tag = this.measureTag();
      const t = (now - this.start) / 1000;

      // ruhiges Schweben nach dem Zusammenbau – keine Reaktion auf Maus, Touch oder Scrollen
      const idle = clamp((t - 4.4) / 2, 0, 1);
      const ry = this.baseRy + Math.sin(t * 0.38) * 10 * idle;
      const rx = this.baseRx + Math.sin(t * 0.55) * 3 * idle;

      // 2) … dann nur noch schreiben
      this.apply(rx, ry);
      this.placeScrewTag(tag);

      this.raf = requestAnimationFrame((n) => this.tick(n));
    }

    // CSS-Animationen (Zusammenbau, Schrauben, Glanz …) pausieren, solange das Case nicht im Bild ist
    setAnimationsPaused(pause) {
      if (!this.getAnimations) return;
      if (pause) {
        this.paused = this.getAnimations({ subtree: true }).filter((a) => a.playState === 'running');
        this.paused.forEach((a) => a.pause());
      } else {
        this.paused.forEach((a) => {
          try {
            a.play();
          } catch (e) {}
        });
        this.paused = [];
      }
    }

    apply(rx, ry) {
      const norm = ((((ry + 180) % 360) + 360) % 360) - 180;
      const sheen = clamp(50 - (norm - this.baseRy) * 2.2, -40, 140);
      // Drehung direkt als transform, Glanz nur auf den 4 Flächen mit Lichtreflex (keine vererbte Variable am ganzen Case)
      this.rig.style.transform = `rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
      const sv = sheen.toFixed(1);
      if (sv !== this._sheen) {
        this._sheen = sv;
        if (!this.sheenEls) this.sheenEls = [...this.rig.querySelectorAll('.tcg-p-front > .f--front, .tcg-p-left > .f--left, .tcg-p-right > .f--right, .tcg-p-top > .f--top')];
        this.sheenEls.forEach((el) => el.style.setProperty('--sheen', sv));
      }
    }

    // Nur Lesen: Position der Schraube relativ zur Bühne (+ Schildgröße, gecacht)
    measureTag() {
      const tag = this.screwTag;
      if (!tag || !this.anchor) return null;
      const s = this.anchor.getBoundingClientRect();
      const st = this.stage.getBoundingClientRect();
      if (!s.width) return null;
      if (!this.tagSize) this.tagSize = { w: tag.offsetWidth, h: tag.offsetHeight };
      return {
        x: s.left + s.width / 2 - st.left,
        y: s.top + s.height / 2 - st.top,
        left: st.left,
        w: this.tagSize.w,
        h: this.tagSize.h,
      };
    }

    // Nur Schreiben: "Sicher verschraubt"-Schild an der Schraube halten
    placeScrewTag(m) {
      const tag = this.screwTag;
      if (!tag || !m) return;
      // label sits left of the screw; switch to the right only if there is no room
      const flip = m.left + m.x - m.w < 8;
      if (flip !== this.tagFlip) {
        tag.classList.toggle('is-right', flip);
        this.tagFlip = flip;
        this.tagSize = null; // Breite kann sich mit der Seite ändern
      }
      tag.style.left = `${(flip ? m.x - 3.5 : m.x - m.w + 3.5).toFixed(1)}px`;
      tag.style.top = `${(m.y - m.h / 2).toFixed(1)}px`;
      if (!this.tagPlaced) {
        tag.classList.add('is-placed');
        this.tagPlaced = true;
      }
    }
  }

  customElements.define('tcg-acrylic-case', TcgAcrylicCase);
})();
